import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/shallow';
import {
  UserNotification,
  NotificationPreferences,
  RecipientStatus,
  NotificationQueryParams,
} from '@/types/notification.types';
import { notificationService } from '@/services/notification.service';

// ============== STATE INTERFACE ==============

interface NotificationState {
  // Notification data
  notifications: UserNotification[];
  unreadCount: number;
  totalCount: number;
  hasMore: boolean;

  // Preferences
  preferences: NotificationPreferences | null;

  // Connection state
  isConnected: boolean;
  connectionStatus: 'connected' | 'connecting' | 'disconnected';

  // Loading states
  isLoading: boolean;
  isLoadingMore: boolean;

  // Error state
  error: string | null;

  // Dropdown state
  isDropdownOpen: boolean;

  // Query params
  currentParams: NotificationQueryParams;
}

// ============== ACTIONS INTERFACE ==============

interface NotificationActions {
  // Notification actions
  setNotifications: (notifications: UserNotification[], total: number) => void;
  addNotification: (notification: UserNotification) => void;
  appendNotifications: (notifications: UserNotification[]) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;

  // Count actions
  setUnreadCount: (count: number) => void;
  decrementUnreadCount: () => void;

  // Preferences actions
  setPreferences: (preferences: NotificationPreferences | null) => void;

  // Connection actions
  setConnectionStatus: (status: 'connected' | 'connecting' | 'disconnected') => void;
  setIsConnected: (isConnected: boolean) => void;

  // Loading actions
  setIsLoading: (isLoading: boolean) => void;
  setIsLoadingMore: (isLoadingMore: boolean) => void;

  // Error actions
  setError: (error: string | null) => void;

  // Dropdown actions
  setDropdownOpen: (isOpen: boolean) => void;
  toggleDropdown: () => void;

  // Query actions
  setCurrentParams: (params: NotificationQueryParams) => void;

  // Reset actions
  reset: () => void;
  clearNotifications: () => void;

  // DB-driven refresh — fetches bell feed + unread count fresh from the API
  // so the store always reflects server truth. Used after any mutation.
  syncFromServer: () => Promise<void>;
}

// ============== INITIAL STATE ==============

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  totalCount: 0,
  hasMore: false,
  preferences: null,
  isConnected: false,
  connectionStatus: 'disconnected',
  isLoading: false,
  isLoadingMore: false,
  error: null,
  isDropdownOpen: false,
  currentParams: {
    skip: 0,
    limit: 20,
  },
};

// ============== STORE ==============

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  persist(
    (set) => ({
      ...initialState,

      // Notification actions
      setNotifications: (notifications, total) => {
        const unreadCount = notifications.filter(
          (n) => n.status !== RecipientStatus.READ
        ).length;

        set({
          notifications,
          totalCount: total,
          unreadCount,
          hasMore: notifications.length < total,
        });
      },

      addNotification: (notification) => {
        set((state) => {
          // Check if notification already exists
          const exists = state.notifications.some((n) => n._id === notification._id);
          if (exists) return state;

          return {
            notifications: [notification, ...state.notifications],
            totalCount: state.totalCount + 1,
            unreadCount:
              notification.status !== RecipientStatus.READ
                ? state.unreadCount + 1
                : state.unreadCount,
          };
        });
      },

      appendNotifications: (notifications) => {
        set((state) => {
          // Filter out duplicates
          const existingIds = new Set(state.notifications.map((n) => n._id));
          const newNotifications = notifications.filter((n) => !existingIds.has(n._id));

          return {
            notifications: [...state.notifications, ...newNotifications],
            hasMore: state.notifications.length + newNotifications.length < state.totalCount,
          };
        });
      },

      markAsRead: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find((n) => n._id === notificationId);
          if (!notification || notification.status === RecipientStatus.READ) {
            return state;
          }

          return {
            notifications: state.notifications.map((n) =>
              n._id === notificationId
                ? { ...n, status: RecipientStatus.READ, read_at: new Date().toISOString() }
                : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({
            ...n,
            status: RecipientStatus.READ,
            read_at: n.read_at || new Date().toISOString(),
          })),
          unreadCount: 0,
        }));
      },

      removeNotification: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find((n) => n._id === notificationId);
          const wasUnread = notification && notification.status !== RecipientStatus.READ;

          return {
            notifications: state.notifications.filter((n) => n._id !== notificationId),
            totalCount: Math.max(0, state.totalCount - 1),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        });
      },

      // Count actions
      setUnreadCount: (count) => set({ unreadCount: count }),

      decrementUnreadCount: () => {
        set((state) => ({
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
      },

      // Preferences actions
      setPreferences: (preferences) => set({ preferences }),

      // Connection actions
      setConnectionStatus: (connectionStatus) => {
        set({
          connectionStatus,
          isConnected: connectionStatus === 'connected',
        });
      },

      setIsConnected: (isConnected) => {
        set({
          isConnected,
          connectionStatus: isConnected ? 'connected' : 'disconnected',
        });
      },

      // Loading actions
      setIsLoading: (isLoading) => set({ isLoading }),
      setIsLoadingMore: (isLoadingMore) => set({ isLoadingMore }),

      // Error actions
      setError: (error) => set({ error }),

      // Dropdown actions
      setDropdownOpen: (isDropdownOpen) => set({ isDropdownOpen }),
      toggleDropdown: () => set((state) => ({ isDropdownOpen: !state.isDropdownOpen })),

      // Query actions
      setCurrentParams: (params) => set({ currentParams: params }),

      // Reset actions
      reset: () => set(initialState),

      clearNotifications: () =>
        set({
          notifications: [],
          unreadCount: 0,
          totalCount: 0,
          hasMore: false,
          currentParams: { skip: 0, limit: 20 },
        }),

      // DB-driven refresh — bell feed endpoint returns only unread + not-cleared.
      // The returned count IS the unread count (feed == unread set).
      syncFromServer: async () => {
        const { data, unread_count } = await notificationService.getBellFeed();
        set({
          notifications: data,
          totalCount: data.length,
          unreadCount: unread_count,
          hasMore: false,
        });
      },
    }),
    {
      name: 'notification-storage',
      // Only persist preferences and some settings
      partialize: (state) => ({
        preferences: state.preferences,
      }),
    }
  )
);

// ============== SELECTORS ==============

// Get recent notifications (for dropdown) - memoized to prevent infinite re-renders
export const useRecentNotifications = (limit = 10) => {
  return useNotificationStore(
    useShallow((state) => state.notifications.slice(0, limit))
  );
};

// Get unread notifications only
export const useUnreadNotifications = () => {
  return useNotificationStore(
    useShallow((state) => state.notifications.filter((n) => n.status !== RecipientStatus.READ))
  );
};

// Get notification by ID
export const useNotificationById = (id: string) => {
  return useNotificationStore((state) =>
    state.notifications.find((n) => n._id === id)
  );
};

// Connection status selector - use shallow comparison for object
export const useNotificationConnection = () => {
  return useNotificationStore(
    useShallow((state) => ({
      isConnected: state.isConnected,
      connectionStatus: state.connectionStatus,
    }))
  );
};

// Unread count selector - primitive, no shallow needed
export const useUnreadCount = () => {
  return useNotificationStore((state) => state.unreadCount);
};

// Loading states selector - use shallow comparison for object
export const useNotificationLoading = () => {
  return useNotificationStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      isLoadingMore: state.isLoadingMore,
    }))
  );
};

// Dropdown state selector - use shallow comparison for object
export const useNotificationDropdown = () => {
  return useNotificationStore(
    useShallow((state) => ({
      isOpen: state.isDropdownOpen,
      setOpen: state.setDropdownOpen,
      toggle: state.toggleDropdown,
    }))
  );
};
